import { Alert, Form, Input, Modal } from 'antd'
import type { Rule } from 'antd/es/form'

interface KeyField {
  label: string
  placeholder?: string
  rules: Rule[]
  normalize?: (value: string) => string
}

interface Props {
  open: boolean
  title: string
  // Names the action on the confirm button; the target is appended.
  action: string
  // Fixed target, or a field the operator fills in.
  target: string | KeyField
  maxReason: number
  // Keys that already have a rule: a submit replaces their reason.
  existing?: string[]
  loading: boolean
  onSubmit: (key: string, reason: string) => Promise<unknown>
  onClose: () => void
}

interface Values {
  key?: string
  reason: string
}

export function ReasonModal({
  open,
  title,
  action,
  target,
  maxReason,
  existing = [],
  loading,
  onSubmit,
  onClose,
}: Props) {
  const [form] = Form.useForm<Values>()
  const typed = Form.useWatch('key', form)
  const fixed = typeof target === 'string'
  const name = fixed ? target : typed?.trim() || '…'
  const replacing = existing.includes(name)

  const submit = async () => {
    const values = await form.validateFields()
    const key = fixed ? target : (values.key ?? '').trim()
    await onSubmit(key, values.reason.trim())
    onClose()
  }

  return (
    <Modal
      title={title}
      open={open}
      okText={replacing ? `Replace reason for ${name}` : `${action} ${name}`}
      onOk={() => void submit().catch(() => undefined)}
      onCancel={onClose}
      confirmLoading={loading}
      destroyOnHidden
    >
      <Form<Values> form={form} layout="vertical" preserve={false}>
        {!fixed && (
          <Form.Item
            name="key"
            label={target.label}
            rules={target.rules}
            normalize={target.normalize}
          >
            <Input placeholder={target.placeholder} />
          </Form.Item>
        )}
        {replacing && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={`${name} already has a rule; this replaces its reason and author.`}
          />
        )}
        <Form.Item
          name="reason"
          label="Reason"
          rules={[
            { required: true, whitespace: true, message: 'Give a reason' },
            { max: maxReason },
          ]}
        >
          <Input.TextArea rows={3} maxLength={maxReason} showCount />
        </Form.Item>
      </Form>
    </Modal>
  )
}
